/**
 * VM-12 Phase B — Dokumentenmappe
 *
 * Zeigt die vollständige Versand-Historie eines Mietverhältnisses/Vorgangs/
 * Objekts/Kontakts inkl. aller Belege (Hauptschreiben, Fensterumschlag,
 * Versandbeleg-Postversand, hochgeladener Einlieferungsbeleg der Deutschen
 * Post) sowie der E-Mail-Ereignisse (Zustellung / gelesen / Bounce).
 *
 * Verwendung:
 *   <DokumentenmappeTab tenancyId={id} />
 *   <DokumentenmappeTab vorgangId={id} />
 *   <DokumentenmappeTab propertyId={id} contactId={id} />
 */
import FolderUploads from "@/components/communication/FolderUploads";
import { useEffect, useRef, useState } from "react";
import api, { API, getToken } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Envelope, EnvelopeOpen, Warning, CheckCircle, PaperPlaneTilt, Package,
  Download, Eye, ArrowClockwise, FileArrowUp, CaretDown, CaretUp,
  Clock, FileText, Truck, Trash,
} from "@phosphor-icons/react";
import DocumentPreviewDrawer from "@/components/DocumentPreviewDrawer";

const EVENT_LABEL = {
  "email.sent": "Versendet",
  "email.delivered": "Zugestellt",
  "email.opened": "Geöffnet",
  "email.clicked": "Link angeklickt",
  "email.bounced": "Zurückgekommen (Bounce)",
  "email.complained": "Als Spam markiert",
  "email.delivery_delayed": "Zustellung verzögert",
};

function StatusBadges({ d }) {
  const channels = d.channels || [];
  const badges = [];
  if (channels.includes("email")) {
    const email = d.email_status_summary;
    let color = "bg-slate-100 text-slate-700";
    let label = "E-Mail: versendet";
    let Icon = PaperPlaneTilt;
    if (d.email_bounced_at) { color = "bg-rose-100 text-rose-800"; label = "E-Mail: Bounce"; Icon = Warning; }
    else if (d.email_complained_at) { color = "bg-rose-100 text-rose-800"; label = "E-Mail: Spam"; Icon = Warning; }
    else if (d.email_opened_at) { color = "bg-emerald-100 text-emerald-800"; label = "E-Mail: gelesen"; Icon = EnvelopeOpen; }
    else if (d.email_delivered_at) { color = "bg-blue-100 text-blue-800"; label = "E-Mail: zugestellt"; Icon = CheckCircle; }
    else if (email === "failed") { color = "bg-rose-100 text-rose-800"; label = "E-Mail: fehlgeschlagen"; Icon = Warning; }
    else if (email === "queued_no_provider") { color = "bg-amber-100 text-amber-800"; label = "E-Mail: nicht versendet (kein Provider)"; Icon = Warning; }
    badges.push(
      <span key="email" className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${color}`} data-testid={`dispatch-email-status-${d.id}`}>
        <Icon size={12} weight="bold" /> {label}
      </span>
    );
  }
  if (channels.includes("post")) {
    const hasReceipt = (d.post_receipts || []).length > 0;
    if (hasReceipt) {
      badges.push(
        <span key="post" className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800" data-testid={`dispatch-post-status-${d.id}`}>
          <Truck size={12} weight="bold" /> Post: eingeliefert
        </span>
      );
    } else {
      badges.push(
        <span key="post" className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid={`dispatch-post-status-${d.id}`}>
          <Package size={12} weight="bold" /> Post: druckbereit
        </span>
      );
    }
  }
  return <div className="flex flex-wrap gap-1.5">{badges}</div>;
}

function DocLink({ doc, label, testid, onPreview }) {
  if (!doc) return null;
  const download = async () => {
    try {
      const res = await fetch(`${API}/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || `${doc.title || "dokument"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download fehlgeschlagen");
    }
  };
  return (
    <div className="flex items-center gap-1">
      <span className="text-xs text-slate-500 min-w-[110px]">{label}:</span>
      <Button size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => onPreview(doc)} data-testid={`${testid}-preview`}>
        <Eye size={12} className="mr-1" />
        <span className="text-xs">Vorschau</span>
      </Button>
      <Button size="sm" variant="ghost" className="h-6 px-1.5" onClick={download} data-testid={`${testid}-download`}>
        <Download size={12} className="mr-1" />
        <span className="text-xs">{doc.original_filename?.slice(0, 40) || "Herunterladen"}</span>
      </Button>
    </div>
  );
}

function DispatchRow({ d, onReload, onPreview }) {
  const [expanded, setExpanded] = useState(false);
  const fileInputRef = useRef(null);

  const uploadReceipt = async (e) => {
    const f = e.target?.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("title", `Einlieferungsbeleg: ${d.subject?.slice(0, 60) || d.id}`);
    fd.append("category", "Versandnachweis");
    fd.append("doc_type", "post_receipt");
    fd.append("direction", "internal");
    fd.append("linked_dispatch_id", d.id);
    if (d.tenancy_id) fd.append("linked_tenancy_id", d.tenancy_id);
    if (d.property_id) fd.append("linked_property_id", d.property_id);
    if (d.contact_id) fd.append("linked_contact_id", d.contact_id);
    if (d.vorgang_id) fd.append("linked_vorgang_id", d.vorgang_id);
    try {
      const res = await fetch(`${API}/documents/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) throw new Error();
      toast.success("Einlieferungsbeleg hochgeladen");
      onReload();
    } catch {
      toast.error("Upload fehlgeschlagen");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const events = d.events || [];

  return (
    <div className="border-b border-slate-100 py-3 px-3 hover:bg-slate-50/60" data-testid={`dispatch-row-${d.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm truncate">{d.subject || "(ohne Betreff)"}</span>
            {d.vorgang_id && (
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">Vorgang</span>
            )}
          </div>
          <div className="text-xs text-slate-500 tabular-nums mt-0.5">
            {formatDate(d.created_at)} · {(d.channels || []).map(c => c === "email" ? "E-Mail" : "Post").join(" / ") || "—"}
            {d.email_to && <span> · {d.email_to}</span>}
          </div>
          <div className="mt-1.5"><StatusBadges d={d} /></div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-1">
            <Button
              size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50"
              onClick={async () => {
                if (!await window._domivusConfirm("Diesen Versand-Eintrag aus der Dokumentenmappe entfernen? Die zugehörigen Dokumente bleiben in der Dokumentenliste erhalten.")) return;
                try {
                  await api.delete(`/messages/dispatches/${d.id}`);
                  toast.success("Versand-Eintrag entfernt");
                  onReload();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Fehler beim Entfernen");
                }
              }}
              title="Versand-Eintrag löschen"
              data-testid={`dispatch-delete-${d.id}`}
            >
              <Trash size={13} />
            </Button>
            <Button
              size="sm" variant="ghost" className="h-7 px-2 text-xs"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`dispatch-toggle-${d.id}`}
            >
              {expanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
              <span className="ml-1">Details</span>
            </Button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pl-3 border-l-2 border-slate-200 space-y-2" data-testid={`dispatch-details-${d.id}`}>
          <div className="space-y-1">
            <DocLink doc={d.main_document} label="Schreiben" testid={`main-${d.id}`} onPreview={onPreview} />
            <DocLink doc={d.envelope_document} label="Fensterumschlag" testid={`env-${d.id}`} onPreview={onPreview} />
            <DocLink doc={d.proof_document} label="Versandbeleg" testid={`proof-${d.id}`} onPreview={onPreview} />
            {(d.post_receipts || []).map((r) => (
              <DocLink key={r.id} doc={r} label="Einlieferungsbeleg" testid={`receipt-${d.id}-${r.id}`} onPreview={onPreview} />
            ))}
          </div>
          {(d.channels || []).includes("post") && (
            <div className="pt-2 mt-2 border-t border-slate-100">
              <input type="file" ref={fileInputRef} className="hidden" accept="image/*,application/pdf" onChange={uploadReceipt} data-testid={`upload-receipt-input-${d.id}`} />
              <Button
                size="sm" variant="outline" className="h-7 text-xs gap-1"
                onClick={() => fileInputRef.current?.click()}
                data-testid={`upload-receipt-btn-${d.id}`}
              >
                <FileArrowUp size={12} /> Einlieferungsbeleg hochladen
              </Button>
              <span className="text-[10px] text-slate-500 ml-2">
                Scan/Foto vom Post-Einlieferungsbeleg (Deutsche Post o. Ä.)
              </span>
            </div>
          )}
          {events.length > 0 && (
            <div className="pt-2 mt-2 border-t border-slate-100">
              <div className="text-[11px] font-semibold uppercase text-slate-500 tracking-wider mb-1.5">
                E-Mail-Verlauf
              </div>
              <div className="space-y-1">
                {events.map((ev) => (
                  <div key={ev.id} className="flex items-center gap-2 text-xs" data-testid={`dispatch-event-${d.id}-${ev.type}`}>
                    <Clock size={10} className="text-slate-400" />
                    <span className="tabular-nums text-slate-500 w-32">{formatDate(ev.created_at)}</span>
                    <span className="text-slate-700">{EVENT_LABEL[ev.type] || ev.type}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DokumentenmappeTab({
  tenancyId,
  vorgangId,
  propertyId,
  contactId,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const buildParams = (cursor) => {
    const params = { limit: 50 };
    if (tenancyId) params.tenancy_id = tenancyId;
    if (vorgangId) params.vorgang_id = vorgangId;
    if (propertyId) params.property_id = propertyId;
    if (contactId) params.contact_id = contactId;
    if (cursor) params.cursor = cursor;
    return params;
  };

  const load = async () => {
    const params = buildParams(null);
    if (!Object.keys(params).some((k) => k !== "limit")) return;
    setLoading(true);
    try {
      const { data } = await api.get("/messages/dispatches", { params });
      // VM-14b: neuer Response { items, next_cursor }; Legacy: array
      const rows = Array.isArray(data) ? data : (data?.items || []);
      const cursor = Array.isArray(data) ? null : (data?.next_cursor || null);
      setItems(rows);
      setNextCursor(cursor);
    } catch {
      setItems([]);
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/messages/dispatches", { params: buildParams(nextCursor) });
      const rows = Array.isArray(data) ? data : (data?.items || []);
      const cursor = Array.isArray(data) ? null : (data?.next_cursor || null);
      setItems((prev) => [...prev, ...rows]);
      setNextCursor(cursor);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [tenancyId, vorgangId, propertyId, contactId]);

  return (
    <div className="space-y-2" data-testid="dokumentenmappe-tab">
      {(tenancyId || propertyId || vorgangId) && (
        <FolderUploads
          scope={tenancyId ? { tenancy_id: tenancyId } : propertyId ? { property_id: propertyId } : { vorgang_id: vorgangId }}
          ownerTarget={tenancyId ? { tenancy_id: tenancyId } : propertyId ? { property_id: propertyId } : { vorgang_id: vorgangId }} />
      )}
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">
          Chronologische Versand-Historie mit allen Belegen — Schreiben, Fensterumschlag,
          Versandbelege und E-Mail-Ereignissen (Zustellung / gelesen / Bounce).
        </div>
        <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={load} data-testid="dispatches-reload">
          <ArrowClockwise size={12} /> Aktualisieren
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm" data-testid="dispatches-list">
        {loading && items.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500">Lade Versand-Historie…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="dispatches-empty">
            <Envelope size={28} className="mx-auto text-slate-300 mb-2" />
            Noch keine Schreiben versendet. Nutzen Sie den <strong>Schreiben</strong>-Button, um einen Brief zu erstellen.
          </div>
        )}
        {items.map((d) => (
          <DispatchRow key={d.id} d={d} onReload={load} onPreview={setPreviewDoc} />
        ))}
        {nextCursor && (
          <div className="p-3 border-t border-slate-100 text-center">
            <Button size="sm" variant="outline" onClick={loadMore} disabled={loadingMore} data-testid="dispatches-load-more">
              {loadingMore ? "Lädt…" : "Weitere anzeigen"}
            </Button>
          </div>
        )}
      </div>

      {previewDoc && (
        <DocumentPreviewDrawer
          docId={previewDoc.id}
          onClose={() => setPreviewDoc(null)}
          metaOverride={{ title: previewDoc.title, original_filename: previewDoc.original_filename }}
          mode="admin"
        />
      )}
    </div>
  );
}
